import { formatPrice, resolveFuturesPeriodSeconds } from '../../utils/futuresProduct';
import { useTranslation } from '../../i18n/LocaleContext';

function TrendIcon({ direction, label }) {
  const isUp = direction === 'UP';
  return (
    <span
      className={`futures-position-card__trend ${isUp ? 'is-up' : 'is-down'}`}
      aria-label={label}
    >
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
        {isUp ? (
          <path
            d="M2 12 L6 7 L9 10 L14 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M2 4 L6 9 L9 6 L14 13"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden>
      <path
        d="M7 3h6l1 2h4v2H3V5h4l1-2zm-1 6h2v7H6V9zm4 0h2v7h-2V9zm4 0h2v7h-2V9z"
        fill="currentColor"
      />
    </svg>
  );
}

function resolveStatusLabel(order, t) {
  if (order.state === 'BOOKED') {
    return t('trade.pendingDraw');
  }
  if (order.state === 'SETTLED') {
    return t('trade.stateSettled');
  }
  if (order.state === 'CANCELLED') {
    return t('trade.stateCancelled');
  }
  return order.stateLabel || order.state || t('common.dash');
}

export default function FuturesPositionCard({
  order,
  product,
  currentPrice,
  cancelling = false,
  onCancel,
}) {
  const { t } = useTranslation();

  if (!order) {
    return null;
  }

  const pairLabel = product?.name
    || product?.displayName
    || (order.marketSymbolCode ? `${order.marketSymbolCode}/USDT` : t('common.dash'));
  const periodSeconds = resolveFuturesPeriodSeconds(product);
  const periodLabel = periodSeconds ? `${periodSeconds}s` : t('common.dash');
  const isBooked = order.state === 'BOOKED';
  const statusLabel = resolveStatusLabel(order, t);
  const strikePrice = order.settlePrice ?? order.orderPrice;
  const showCancel = isBooked && typeof onCancel === 'function';
  const trendLabel = order.orderTrend === 'UP' ? t('trade.buyUp') : t('trade.buyDown');
  const dash = t('common.dash');

  return (
    <li className="futures-position-card">
      <div className="futures-position-card__head">
        <div className="futures-position-card__symbol">
          <strong>{pairLabel}</strong>
          <TrendIcon direction={order.orderTrend} label={trendLabel} />
        </div>
        <div className="futures-position-card__actions">
          <span className={`futures-position-card__status ${isBooked ? 'is-pending' : ''}`}>
            {statusLabel}
          </span>
          {showCancel && (
            <button
              type="button"
              className="futures-position-card__cancel"
              aria-label={t('trade.cancelOrder')}
              disabled={cancelling}
              onClick={() => onCancel(order)}
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      <div className="futures-position-card__grid">
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.settlementTime')}</span>
          <span className="futures-position-card__value">{periodLabel}</span>
        </div>
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.orderAmount')}</span>
          <span className="futures-position-card__value">{formatPrice(order.orderAmount, 6)}</span>
        </div>
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.fillPrice')}</span>
          <span className="futures-position-card__value">
            {isBooked ? dash : formatPrice(strikePrice, 4)}
          </span>
        </div>
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.currentPrice')}</span>
          <span className="futures-position-card__value">{formatPrice(currentPrice, 4)}</span>
        </div>
      </div>

      <div className="futures-position-card__foot">
        <div className="futures-position-card__times">
          <span className="futures-position-card__clock" aria-hidden>
            🕐
          </span>
          <div>
            <div className="futures-position-card__time-main">{order.roundEndTime || dash}</div>
            <div className="futures-position-card__time-sub">{order.orderTime || dash}</div>
          </div>
        </div>
        <div className="futures-position-card__summary">
          <div>
            {t('trade.profitLoss')}：
            <strong>{order.profitLoss != null ? formatPrice(order.profitLoss, 6) : dash}</strong>
          </div>
          <div>
            {t('trade.fee')}：
            <strong>{formatPrice(order.fee, 6)}</strong>
          </div>
        </div>
      </div>
    </li>
  );
}
